/**
 * Client-only storage gates for exit survey auto-triggers.
 * SSR: all functions no-op or return false when `window` is missing.
 */

export type ExitSurveyTriggerId = "exit_intent" | "idle" | "scroll_depth" | "leave_cart";

/** Idle: no key/pointer/scroll activity on window for this long → fire. */
export const EXIT_SURVEY_IDLE_MS = 45_000;

/** After a successful submit, suppress auto-triggers (localStorage). */
export const EXIT_SURVEY_SUBMIT_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/** Skip / close without submit: suppress across tabs until this elapses (localStorage). */
export const EXIT_SURVEY_SNOOZE_MS = 24 * 60 * 60 * 1000;

const KEY_SESSION_AUTO_OPENED = "exit_survey_auto_opened_v2";
const KEY_SNOOZE_UNTIL = "exit_survey_snooze_until";
const KEY_LAST_SUBMIT_AT = "exit_survey_last_submit_at";

/** Set while pathname === "/cart" && cart has line items; consumed when navigating away. */
export const EXIT_SURVEY_LEAVE_CART_PENDING = "exit_survey_leave_cart_pending";

function readLocalNumber(key: string): number | null {
    try {
        const s = localStorage.getItem(key);
        if (!s) return null;
        const n = Number.parseInt(s, 10);
        return Number.isFinite(n) ? n : null;
    } catch {
        return null;
    }
}

export function canShowExitSurvey(): boolean {
    if (typeof window === "undefined") return false;
    try {
        if (sessionStorage.getItem(KEY_SESSION_AUTO_OPENED) === "1") return false;
        const snoozeUntil = readLocalNumber(KEY_SNOOZE_UNTIL);
        if (snoozeUntil !== null && Date.now() < snoozeUntil) return false;
        const lastSubmitAt = readLocalNumber(KEY_LAST_SUBMIT_AT);
        if (lastSubmitAt !== null && Date.now() - lastSubmitAt < EXIT_SURVEY_SUBMIT_TTL_MS) return false;
        return true;
    } catch {
        return false;
    }
}

export function markExitSurveySessionAutoOpened() {
    try {
        sessionStorage.setItem(KEY_SESSION_AUTO_OPENED, "1");
    } catch {
        /* ignore */
    }
}

export function markExitSurveySnoozed() {
    try {
        localStorage.setItem(KEY_SNOOZE_UNTIL, String(Date.now() + EXIT_SURVEY_SNOOZE_MS));
    } catch {
        /* ignore */
    }
}

export function markExitSurveySubmitted() {
    try {
        localStorage.setItem(KEY_LAST_SUBMIT_AT, String(Date.now()));
    } catch {
        /* ignore */
    }
}
